/**
 * Save flow for the Candid notes modal.
 *
 * Maps {@link CandidNotesFormState} onto {@link CandidNotesUploadRequest} and
 * POSTs it via {@link CandidService.postNote}. Failures set `submitError` and
 * an error toast; success invokes `onSuccess` so the caller can refetch,
 * close, and toast.
 *
 * @module useCandidNotesUploadSave
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useState } from "react";
import { CandidNotesUploadRequest } from "../../../../api/services/candid/candidApiTypes";
import { CandidService } from "../../../../api/services/candid/candidService";
import { CandidNotesFormState } from "../../../../data-model/candidNotesFormState";
import { ToastMessages } from "../../../toasts/toastMessages";

/** Order-level identifiers the notes form does not carry. */
export interface CandidNotesUploadContext {
  /** PA order identifier. */
  orderId: string;
  /** Organisation identifier. */
  orgId: string;
  /** Patient MRN from `generalOrderDetails.demographics`. */
  mrn: string;
}

/**
 * Props for {@link useCandidNotesUploadSave}.
 */
interface UseCandidNotesUploadSaveProps {
  /** Latest notes snapshot from {@link PatientNotesBody}. */
  formState: CandidNotesFormState;
  /** Order / org / MRN for the request body. */
  context: CandidNotesUploadContext;
  /** Invoked after a successful POST (refetch, close, toast). */
  onSuccess: () => void | Promise<void>;
}

/**
 * Maps the notes snapshot and order context onto the notes-upload request.
 *
 * @param formState - Notes text from the modal body
 * @param context - Order, organisation, and MRN identifiers
 * @returns Body for {@link CandidService.postNote}
 */
const buildCandidNotesUploadRequest = (
  formState: CandidNotesFormState,
  context: CandidNotesUploadContext,
): CandidNotesUploadRequest => ({
  order_id: context.orderId,
  org_id: context.orgId,
  mrn: context.mrn,
  note: formState.notes.trim(),
});

/**
 * Posts the note to Candid and tracks in-flight / error state.
 *
 * @param props.formState - Current notes snapshot
 * @param props.context - Order-level request fields
 * @param props.onSuccess - Called after a successful upload
 * @returns Save handler, disabled flag, button label, and error message
 */
export const useCandidNotesUploadSave = ({
  formState,
  context,
  onSuccess,
}: UseCandidNotesUploadSaveProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  /**
   * Transforms the form and POSTs it. No-ops while invalid or already in flight.
   * On success, calls `onSuccess`. On failure, records `submitError` and toasts.
   *
   * @throws Surfaces HTTP failures as `submitError` and an error toast; does not rethrow.
   */
  const handleSave = useCallback(async () => {
    if (!formState.isValid || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await CandidService.postNote(
        buildCandidNotesUploadRequest(formState, context),
      );
      await onSuccess();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : ToastMessages.CANDID_NOTES_UPLOAD_ERROR;
      setSubmitError(message);
      toast.error(ToastMessages.CANDID_NOTES_UPLOAD_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  }, [formState, context, isSubmitting, onSuccess]);

  const isSaveDisabled = !formState.isValid || isSubmitting;
  const saveButtonText = isSubmitting ? "Uploading..." : "Trigger Upload";

  return {
    handleSave,
    isSaveDisabled,
    saveButtonText,
    isSubmitting,
    submitError,
  };
};
